import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { useLearnerEmails } from "@/components/LearnerEmailsSwitch";
import { EmailOptInToggle } from "@/components/EmailOptInToggle";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Send, Ban, RotateCcw } from "lucide-react";
import { EoiDetails } from "@/components/t4r/learners/EoiDetails";

export const ManageLearnerDialog = ({ learner, courses, onClose, onChanged }) => {
  const [emailsOn] = useLearnerEmails("retrofit");
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setIds(learner?.course_ids || []); }, [learner]);
  if (!learner) return null;

  const suspended = learner.status === "archived";
  const toggle = (id) => setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const saveCourses = () => run(async () => { await api.put(`/lms/admin/learners/${learner.id}/courses`, { course_ids: ids }); onClose(); }, "Enrolments updated");
  const setStatus = () => run(() => api.put(`/lms/admin/learners/${learner.id}/status`, { active: suspended }), suspended ? "Learner reactivated" : "Learner suspended");
  const resend = () => run(() => api.post(`/lms/admin/learners/${learner.id}/resend-invite`), `Invite resent to ${learner.email}`);

  return (
    <Dialog open={!!learner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="manage-learner-dialog">
        <DialogHeader><DialogTitle>{learner.name}</DialogTitle><DialogDescription className="text-xs">Manage which courses this learner can see and their portal access.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{learner.email}</span>
            <Badge variant="outline" className="text-[10px] capitalize" data-testid="manage-learner-status">{suspended ? "suspended" : learner.status}</Badge>
          </div>
          <div>
            <Label className="text-xs">Enrolled courses — learners only ever see these</Label>
            <div className="max-h-[200px] overflow-y-auto border border-border rounded-md bg-white mt-1 divide-y divide-slate-50">
              {courses.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-green-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`manage-course-${c.id}`} />
                  <span className="truncate flex-1">{c.title}</span>
                  {!c.is_published && <span className="text-[10px] text-slate-400">draft</span>}
                </label>
              ))}
              {courses.length === 0 && <p className="p-3 text-xs text-muted-foreground italic">No courses yet.</p>}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">{emailsOn === false && !learner.email_opt_in ? "Learner emails are off, so no enrolment email is sent." : "Newly added courses trigger an enrolment email."} Removing a course cuts access straight away; past attempts stay visible to staff only.</p>
          </div>
          <EmailOptInToggle url={`/lms/admin/learners/${learner.id}/email-opt-in`} value={learner.email_opt_in} name={learner.name} onChanged={onChanged} />
          <EoiDetails learner={learner} onChanged={onChanged} />
          <div className="flex flex-wrap gap-2">
            {["invited", "applicant"].includes(learner.status) && <Button size="sm" variant="outline" className="gap-1 text-xs" disabled={busy} onClick={resend} data-testid="manage-resend-invite"><Send size={12} /> {learner.status === "applicant" ? "Send portal invite" : "Resend invite"}</Button>}
            <Button size="sm" variant="outline" className={`gap-1 text-xs ${suspended ? "text-green-700 border-green-200" : "text-rose-600 border-rose-200"}`} disabled={busy} onClick={setStatus} data-testid="manage-toggle-status">
              {suspended ? <><RotateCcw size={12} /> Reactivate</> : <><Ban size={12} /> Suspend access</>}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-green-700 hover:bg-green-800 text-white" disabled={busy} onClick={saveCourses} data-testid="manage-save-courses">Save enrolments</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
